/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Combobox, useCombobox, Pill, PillsInput, Text, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { masterCategoryService } from 'services/lia/masterCategoryService';

interface CategorySelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  error,
  label = 'Question Categories',
  placeholder = 'Select or type categories',
  required = false
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active')
  });

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  // Always fetch categories - use empty string for initial load, then use debounced search
  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['masterCategories', debouncedSearch],
    queryFn: () =>
      masterCategoryService.getList({
        search: debouncedSearch || '' // Use empty string if no search
      }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000 // Keep in cache for 10 minutes
  });

  // Extract unique categories from the response
  const availableCategories = React.useMemo(() => {
    if (!categoriesData?.data) return [];

    const allCategories = new Set<string>();

    categoriesData.data.forEach((item) => {
      // Split categories by comma and trim whitespace
      const categories = item.category.split(',').map((cat) => cat.trim());
      categories.forEach((cat) => {
        if (cat) allCategories.add(cat);
      });
    });

    return Array.from(allCategories).sort();
  }, [categoriesData]);

  // Filter options based on search and exclude already selected values
  const filteredOptions = availableCategories.filter(
    (option) => option.toLowerCase().includes(search.toLowerCase()) && !value.includes(option)
  );

  const handleValueSelect = (selectedValue: string) => {
    const trimmedValue = selectedValue.trim();
    if (trimmedValue && !value.includes(trimmedValue)) {
      onChange([...value, trimmedValue]);
    }
    setSearch('');
    combobox.closeDropdown();
  };

  const handleValueRemove = (valueToRemove: string) => {
    onChange(value.filter((v) => v !== valueToRemove));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && search.trim()) {
      event.preventDefault();
      handleValueSelect(search);
    }
  };

  const values = value.map((item) => (
    <Pill key={item} withRemoveButton onRemove={() => handleValueRemove(item)}>
      {item}
    </Pill>
  ));

  const options = filteredOptions.map((item) => (
    <Combobox.Option value={item} key={item}>
      {item}
    </Combobox.Option>
  ));

  // Show "Add custom category" option when search doesn't match any existing category
  const showCustomOption =
    search.trim() &&
    !availableCategories.some((cat) => cat.toLowerCase() === search.toLowerCase()) &&
    !value.includes(search.trim());

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={false}>
      <Combobox.DropdownTarget>
        <PillsInput
          label={label}
          required={required}
          error={error}
          onClick={() => combobox.openDropdown()}
        >
          <Pill.Group>
            {values}
            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                onChange={(event) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder={value.length > 0 ? '' : placeholder}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          {isLoading ? (
            <Combobox.Option value='' disabled>
              <Loader size='sm' /> Loading categories...
            </Combobox.Option>
          ) : options.length > 0 ? (
            <>
              {options}
              {showCustomOption && (
                <Combobox.Option value={search}>
                  <Text>Add "{search}"</Text>
                </Combobox.Option>
              )}
            </>
          ) : showCustomOption ? (
            <Combobox.Option value={search}>
              <Text>Add "{search}"</Text>
            </Combobox.Option>
          ) : search.trim() ? (
            <Combobox.Empty>No categories found</Combobox.Empty>
          ) : availableCategories.length === 0 ? (
            <Text size='sm' c='dimmed' p='xs'>
              No categories available. Type to add new ones.
            </Text>
          ) : (
            <Text size='sm' c='dimmed' p='xs'>
              Type to search categories or add new ones
            </Text>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default CategorySelector;
